/**
 * NewsletterSubscribersDialog — Super-Admin-only modal showing newsletter
 * subscribers separately from registered users. Searchable + CSV export.
 */
import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Search, Download, Mail, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

export default function NewsletterSubscribersDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(async (p = page, query = q) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('actanonedu_session_token');
      const url = new URL(`${API}/api/super-admin/newsletter`);
      url.searchParams.set('page', p);
      url.searchParams.set('page_size', PAGE_SIZE);
      if (query) url.searchParams.set('q', query);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, q]);

  useEffect(() => { if (open) fetchPage(1, ''); }, [open, fetchPage]);

  const exportCsv = async () => {
    const token = localStorage.getItem('actanonedu_session_token');
    const res = await fetch(`${API}/api/super-admin/newsletter/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const text = await res.text();
    const blob = new Blob([text], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'newsletter_subscribers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="newsletter-subscribers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-indigo-600" /> Newsletter Subscribers</DialogTitle>
          <DialogDescription>People who opted into the marketing newsletter — separate from registered users.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchPage(1, q)}
              placeholder="Search email…"
              className="pl-9 h-10"
              data-testid="newsletter-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchPage(1, q)} disabled={loading} data-testid="newsletter-search-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="newsletter-export-csv">
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
        </div>
        <div className="text-xs text-slate-500 mb-2">{total.toLocaleString()} subscribers total</div>
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_120px_100px_140px] bg-slate-50 dark:bg-slate-900 px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500">
            <div>Email</div><div>Source</div><div>Status</div><div>Subscribed at</div>
          </div>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {loading ? (
              <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">No subscribers found.</div>
            ) : items.map((s) => (
              <div key={s.email} className="grid grid-cols-[1fr_120px_100px_140px] px-4 py-2.5 text-sm items-center">
                <div className="font-medium truncate" title={s.email}>{s.email}</div>
                <div className="text-xs text-slate-500 truncate">{s.source || '—'}</div>
                <div><Badge variant={s.active ? 'default' : 'secondary'} className={s.active ? 'bg-emerald-500/90' : ''}>{s.active ? 'Active' : 'Unsubscribed'}</Badge></div>
                <div className="text-xs text-slate-500">{(s.createdAt || '').slice(0, 10)}</div>
              </div>
            ))}
          </div>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between mt-3 text-sm">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage((p) => p - 1); fetchPage(page - 1, q); }}>Prev</Button>
            <span className="text-slate-500">Page {page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => { setPage((p) => p + 1); fetchPage(page + 1, q); }}>Next</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
